import React, { useState } from 'react';
import { Timer, ChevronDown } from 'lucide-react';

export const fmtDur = (secs) => {
  if (secs == null || !isFinite(secs) || secs < 0) return '—';
  const s = Math.round(secs);
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h} h ${m} min`;
  return `${m}:${String(r).padStart(2, '0')} min`;
};

export const jobTiming = (j) => {
  const start = j.claimed_at ? new Date(j.claimed_at).getTime() : null;
  const rep = j.reported_at ? new Date(j.reported_at).getTime() : 0;
  const est = j.est_done_at ? new Date(j.est_done_at).getTime() : 0;
  const end = Math.max(rep, est) || null;
  if (!start || !end || end < start) return null;
  return { start, end, secs: (end - start) / 1000 };
};

const DOC_LABEL = { letter: 'Brief', flyer: 'Flyer', envelope: 'Umschlag' };

export const BatchTimingSummary = ({ jobs, now }) => {
  const [open, setOpen] = useState(false);
  const withBatch = jobs.filter(j => j.batch_id);
  if (!withBatch.length) return null;
  const latest = withBatch[0].batch_id;
  const b = withBatch.filter(j => j.batch_id === latest);
  const stillWriting = j => j.status === 'printed' && j.est_done_at && new Date(j.est_done_at).getTime() > now;
  const active = b.some(j => ['hold', 'queued', 'sent'].includes(j.status) || stillWriting(j));
  const printed = b.filter(j => j.status === 'printed');
  if (active || !printed.length) return null;
  const timed = printed.map(j => ({ j, t: jobTiming(j) })).filter(x => x.t);
  if (!timed.length) return null;

  const runSecs = (Math.max(...timed.map(x => x.t.end)) - Math.min(...timed.map(x => x.t.start))) / 1000;
  const errors = b.filter(j => j.status === 'error').length;

  const groups = new Map();
  for (const { j, t } of timed) {
    const key = j.batch_seq > 0 ? `s${j.batch_seq}` : (j.letter_id || j.id);
    if (!groups.has(key)) groups.set(key, { seq: j.batch_seq || 0, name: j.company_name || '—', docs: {}, total: 0 });
    const g = groups.get(key);
    g.docs[j.doc_type || 'letter'] = (g.docs[j.doc_type || 'letter'] || 0) + t.secs;
    g.total += t.secs;
  }
  const rows = [...groups.values()].sort((a, b2) => (a.seq || 999) - (b2.seq || 999));
  const avg = (type) => {
    const vals = rows.map(g => g.docs[type]).filter(Boolean);
    return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : null;
  };
  const avgLetter = avg('letter'), avgFlyer = avg('flyer');

  return (
    <div className="mx-4 my-2 border border-emerald-500/40 bg-emerald-500/5 rounded-sm" data-testid="batch-timing-summary">
      <button onClick={() => setOpen(v => !v)} data-testid="batch-timing-toggle"
        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-emerald-500/10 transition-colors">
        <Timer className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="text-xs font-medium text-emerald-300">Produktion abgeschlossen</span>
        <span className="text-[10px] font-mono text-muted-foreground truncate" data-testid="batch-timing-total">
          {timed.length} Dokumente in {fmtDur(runSecs)}
          {avgLetter ? ` · Ø Brief ${fmtDur(avgLetter)}` : ''}
          {avgFlyer ? ` · Ø Flyer ${fmtDur(avgFlyer)}` : ''}
          {errors ? ` · ${errors} Fehler` : ''}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground/60 ml-auto shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="border-t border-emerald-500/20" data-testid="batch-timing-rows">
          <div className="divide-y divide-border/40 max-h-52 overflow-y-auto">
            {rows.map((g, i) => (
              <div key={i} className="px-3 py-1 flex items-center gap-2 text-xs" data-testid={`batch-timing-row-${i}`}>
                {g.seq > 0 && <span className="text-[10px] text-muted-foreground font-mono shrink-0">#{g.seq}</span>}
                <span className="text-foreground truncate">{g.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground truncate">
                  {Object.entries(g.docs).map(([dt, s]) => `${DOC_LABEL[dt] || dt} ${fmtDur(s)}`).join(' · ')}
                </span>
                <span className="ml-auto text-[10px] font-mono text-emerald-300/90 shrink-0">{fmtDur(g.total)}</span>
              </div>
            ))}
          </div>
          <p className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border/40">
            Maschinenzeit je Dokument = Übertragung + Schreiben · Gesamtlaufzeit = Start des ersten bis Ende des letzten Dokuments
          </p>
        </div>
      )}
    </div>
  );
};
